<template>
  <div class="choice-mapping-modal">
    <p class="text-muted text-sm mb-3">
      Map elke keuze van de configurator naar de leverancierscode.
    </p>

    <div class="choice-mapping-modal__table">
      <div class="choice-mapping-modal__row choice-mapping-modal__row--header">
        <div class="choice-mapping-modal__col--label">Keuze</div>
        <div class="choice-mapping-modal__col--code">Leverancierscode</div>
      </div>

      <div
        v-for="choice in optionChoices"
        :key="choice.value || choice.id"
        class="choice-mapping-modal__row"
      >
        <div class="choice-mapping-modal__col--label">
          {{ choice.label || choice.value || choice.id }}
        </div>
        <div class="choice-mapping-modal__col--code">
          <TextInput
            :id="`choice-${choice.value || choice.id}`"
            :modelValue="localMappings[choice.value || choice.id] || ''"
            @update:modelValue="localMappings[choice.value || choice.id] = $event"
            :placeholder="`bijv. ${supplierCode}.01`"
            size="sm"
          />
        </div>
      </div>
    </div>

    <div class="choice-mapping-modal__default mt-3">
      <TextInput
        id="choice-default"
        v-model="localDefault"
        label="Default waarde (als geen keuze gemapt)"
        placeholder="bijv. 08.21"
        size="sm"
      />
    </div>

    <div class="choice-mapping-modal__actions mt-3">
      <Btn variant="secondary" size="sm" @click="autoFillFromCatalog" :disabled="!hasCatalogSuggestions">
        Auto-fill vanuit catalogus
      </Btn>
    </div>

    <div class="modal-actions mt-3">
      <Btn variant="primary" @click="handleSubmit">Opslaan</Btn>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import TextInput from '@/components/inputs/TextInput.vue';
import Btn from '@/components/partials/Btn.vue';

export default defineComponent({
  name: 'ChoiceMappingModal',
  components: { TextInput, Btn },

  props: {
    mapping: { type: Object, required: true },
    optionChoices: { type: Array, default: () => [] },
    catalogFields: { type: Array, default: () => [] },
    supplierCode: { type: String, default: '' },
    onSubmit: { type: Function, default: null },
    onCancel: { type: Function, default: null },
  },

  setup(props) {
    const localMappings = ref({});
    const localDefault = ref('');

    const hasCatalogSuggestions = computed(() => {
      return props.catalogFields.length > 0 && props.supplierCode;
    });

    onMounted(() => {
      localMappings.value = { ...(props.mapping.choiceMappings || {}) };
      localDefault.value = props.mapping.defaultValue || '';
    });

    function autoFillFromCatalog() {
      const codeField = props.catalogFields.find(
        (f) => f.code === props.supplierCode || f.supplier_code === props.supplierCode
      );
      if (!codeField?.values) return;

      for (const choice of props.optionChoices) {
        const key = choice.value || choice.id;
        if (localMappings.value[key]) continue;

        const match = codeField.values.find(
          (v) =>
            v.label?.toLowerCase() === (choice.label || choice.value || '').toLowerCase() ||
            v.value?.toLowerCase() === (choice.label || choice.value || '').toLowerCase()
        );
        if (match) {
          localMappings.value[key] = match.code || match.value;
        }
      }
    }

    function handleSubmit() {
      if (props.onSubmit) {
        props.onSubmit({
          choiceMappings: { ...localMappings.value },
          defaultValue: localDefault.value || null,
        });
      }
    }

    return {
      localMappings,
      localDefault,
      hasCatalogSuggestions,
      autoFillFromCatalog,
      handleSubmit,
    };
  },
});
</script>

<style scoped lang="scss">
.choice-mapping-modal {
  &__table {
    border: 1px solid var(--border-color);
    border-radius: 0.5rem;
    overflow: hidden;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid var(--border-color-light);

    &:last-child { border-bottom: none; }

    &--header {
      background: var(--bg-secondary);
      font-weight: 600;
      font-size: 0.8125rem;
      color: var(--text-secondary);
    }
  }

  &__col--label {
    flex: 1;
    font-size: 0.875rem;
  }

  &__col--code {
    flex: 1;
    max-width: 200px;
  }

  &__default {
    max-width: 300px;
  }

  &__actions {
    display: flex;
    gap: 0.5rem;
  }
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.mt-3 { margin-top: 1rem; }
.mb-3 { margin-bottom: 1rem; }
.text-muted { color: var(--text-secondary); }
.text-sm { font-size: 0.8125rem; }
</style>
